type Nullable<T> = T | null;

let audioEl: Nullable<HTMLAudioElement> = null;
let audioCtx: Nullable<AudioContext> = null;
let unlocked = false;

const STORAGE_KEYS = {
  volume: 'notification_volume',
  muted: 'notification_muted',
} as const;

export function getNotificationVolume(): number {
  const raw = localStorage.getItem(STORAGE_KEYS.volume);
  const v = raw ? Number(raw) : 0.7;
  return Math.min(1, Math.max(0, v));
}

export function setNotificationVolume(v: number): void {
  const val = Math.min(1, Math.max(0, v));
  localStorage.setItem(STORAGE_KEYS.volume, String(val));
  if (audioEl) audioEl.volume = val;
}

export function isNotificationMuted(): boolean {
  return localStorage.getItem(STORAGE_KEYS.muted) === '1';
}

export function setNotificationMuted(muted: boolean): void {
  localStorage.setItem(STORAGE_KEYS.muted, muted ? '1' : '0');
  if (audioEl) audioEl.muted = muted;
}

function ensureAudioElement(): HTMLAudioElement {
  if (!audioEl) {
    audioEl = new Audio('/notification.mp3');
    audioEl.preload = 'auto';
    audioEl.volume = getNotificationVolume();
    audioEl.muted = isNotificationMuted();
  }
  return audioEl;
}

function ensureAudioContext(): AudioContext {
  if (!audioCtx) {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    audioCtx = new Ctx();
  }
  return audioCtx;
}

export function initNotificationSound(): void {
  if (unlocked) return;
  const unlock = () => {
    try {
      ensureAudioElement();
      const ctx = ensureAudioContext();
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      unlocked = true;
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    } catch {
      /* noop */
    }
  };
  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });
}

function beepFallback(): void {
  try {
    const ctx = ensureAudioContext();
    const durationMs = 140;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = 880;
    const vol = getNotificationVolume();
    const target = isNotificationMuted() ? 0 : vol;
    gainNode.gain.setValueAtTime(target, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(Math.max(0.001, target * 0.05), ctx.currentTime + durationMs / 1000);
    oscillator.connect(gainNode).connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + durationMs / 1000);
  } catch {
    /* noop */
  }
}

export async function playNotificationSound(): Promise<void> {
  if (isNotificationMuted()) return;
  try {
    const el = ensureAudioElement();
    el.currentTime = 0;
    await el.play();
  } catch {
    beepFallback();
  }
}
